import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useProjects } from '../context/ProjectsContext';
import ProjectGrid from '../components/ProjectGrid';
import LoadingSpinner from '../components/LoadingSpinner';

const Home = () => {
  const { getFeaturedProjects, loading } = useProjects();
  const featuredProjects = getFeaturedProjects();
  const [activeTab, setActiveTab] = useState('professional');

  // M.Arch Portfolio project data
  const marchProject = {
    id: 'march-portfolio',
    'Project Name': 'M.Arch Portfolio – Y2021',
    'Project Overview': "Discover a collection of architectural projects from my Master's degree at Florida International University, completed 2021, reflecting a strong foundation in design thinking and technical execution.",
    'Project Image 1': 'https://content.raheem.work/file/RB/College-Architectural-Projects/M.Arch-Portfolio-Cover-Photo.jpg',
    isExternal: true,
    externalUrl: 'https://link.raheem.work/m-arch-portfolio'
  };

  const displayProjects = activeTab === 'professional' ? featuredProjects : [marchProject];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen"
    >
      {/* Hero Section */}
      <section className="relative bg-white py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <h1 className="font-poppins text-5xl lg:text-6xl font-bold text-primary mb-6">
                Raheem Bishop
              </h1>
              <h2 className="font-montserrat text-2xl lg:text-3xl text-accent-primary mb-8">
                Architect
              </h2>
              <p className="font-barlow text-lg text-tertiary mb-8 leading-relaxed">
                Licensed architect with over 9 years' experience designing innovative spaces 
                in retail, office and commercial sectors.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/projects"
                  className="inline-flex items-center justify-center px-8 py-3 bg-accent-primary text-white font-barlow font-medium rounded-lg hover:bg-accent-secondary transition-colors duration-300"
                >
                  View Portfolio
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-3 border-2 border-accent-primary text-accent-primary font-barlow font-medium rounded-lg hover:bg-accent-primary hover:text-white transition-all duration-300"
                >
                  Get In Touch
                </Link>
              </div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="relative"
            >
              <div className="w-full h-[600px] bg-gray-100 rounded-lg overflow-hidden">
                <iframe
                  src="https://content.raheem.work/file/RB/Logos-and-Favicons/RB-Professional-Photo.html?player=DASZeph"
                  className="w-full h-full border-0"
                  title="Raheem Bishop Professional Portrait"
                  style={{ 
                    objectFit: 'cover',
                    overflow: 'hidden'
                  }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-poppins text-4xl font-bold text-primary mb-4">
              Featured Projects
            </h2>
            <p className="font-barlow text-lg text-tertiary max-w-2xl mx-auto">
              {activeTab === 'march' 
                ? 'View my Masters Degree portfolio.' 
                : 'Explore a selection of my recent work spanning various sectors including retail, office and commercial projects.'
              }
            </p>
          </motion.div>

          {/* Tab Selector */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex justify-center mb-12"
          >
            <div className="bg-gray-100 p-1 rounded-lg inline-flex">
              <button
                onClick={() => setActiveTab('professional')}
                className={`px-6 py-3 font-barlow font-medium rounded-md transition-all duration-300 ${
                  activeTab === 'professional'
                    ? 'bg-accent-primary text-white shadow-md'
                    : 'text-accent-primary hover:bg-gray-200'
                }`}
              >
                Professional Work
              </button>
              <button
                onClick={() => setActiveTab('march')}
                className={`px-6 py-3 font-barlow font-medium rounded-md transition-all duration-300 ${
                  activeTab === 'march'
                    ? 'bg-accent-primary text-white shadow-md'
                    : 'text-accent-primary hover:bg-gray-200'
                }`}
              >
                M.Arch Portfolio
              </button>
            </div>
          </motion.div>

          {loading ? (
            <LoadingSpinner />
          ) : (
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {activeTab === 'march' ? (
                <div className="w-full">
                  <div 
                    onClick={() => window.open(marchProject.externalUrl, '_blank')}
                    className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer group hover:transform hover:scale-105"
                  >
                    <div className="bg-quaternary overflow-hidden h-64">
                      <img
                        src={marchProject['Project Image 1']}
                        alt={marchProject['Project Name']}
                        className="w-full h-full object-cover object-bottom group-hover:opacity-90 transition-opacity duration-300"
                      />
                    </div>
                    
                    <div className="p-8">
                      <h3 className="font-blanka text-2xl font-bold text-primary mb-4 group-hover:text-accent-primary transition-colors duration-300">
                        {marchProject['Project Name']}
                      </h3>
                      
                      <p className="font-barlow text-tertiary mb-6 text-lg leading-relaxed">
                        {marchProject['Project Overview']}
                      </p>
                      
                      <div className="inline-flex items-center text-accent-primary font-barlow font-medium group-hover:text-accent-secondary transition-colors duration-300">
                        View Portfolio →
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <ProjectGrid projects={displayProjects} />
                  
                  <div className="text-center mt-12">
                    <Link
                      to="/projects"
                      className="inline-flex items-center justify-center px-8 py-3 bg-accent-primary text-white font-barlow font-medium rounded-lg hover:bg-accent-secondary transition-colors duration-300"
                    >
                      View All Projects
                    </Link>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </div>
      </section>
    </motion.div>
  );
};

export default Home;